import React, { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useAppActions, useAppState } from '../../../overmind';
import { useNavigate } from 'react-router';
import { KeplerState } from '../../../models/kepler-state';
import { AuthenticationStatus as STATUS } from '../../../enums/authentication-status';
import { ResetPassword } from '../../../models/view/reset-password';
import { AccountPageLoaderLayer } from '../../../components/general/loading-state/loader-layers/account-page-loader-layer/account-page-loader-layer';
import { FetchType } from '../../../enums';
import { PagePath } from '../../../navigation/navigation.enums';
import { Button, ConfirmationPasswordValidator, EmailValidator, FieldController, RequiredValidator, TextField } from '@keplerco/core';
import { PasswordStrength } from '../password-strength/password-strength';
import accountStyles from '../account.module.css';
import { themedAssetUrl } from '../../../library/helpers/themed-asset-url';
import classNames from 'classnames';

export function ResetPasswordPage(): JSX.Element {
  const actions = useAppActions();
  const { authenticationStatus, passwordErrors } = useAppState<KeplerState>();
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const [fieldValues, setFieldValues] = useState<FieldValues>();
  const { control, handleSubmit, getValues, watch } = useForm({ mode: 'onBlur', reValidateMode: 'onBlur' });

  useEffect(() => {
    watch(setFieldValues);
  }, []);

  return (
    <div className={accountStyles.accountBackground} style={{ backgroundImage: `url(${themedAssetUrl('backgrounds/pattern.background.svg')})` }}>
      <div className={accountStyles.accountPage}>
        <div className={classNames('card', accountStyles.accountCard)}>
          <AccountPageLoaderLayer path={PagePath.accountResetPassword}>
            <h1>Reset your password</h1>
            <div style={{ marginTop: 12 }}>Enter the email address that you signed up with and confirm your new password</div>

            <form
              id="resetPassword"
              className={accountStyles.accountForm}
              autoComplete="off"
              onSubmit={handleSubmit(async fieldValues => {
                fieldValues.code = params.get('code')?.toString();
                actions.startLoader({ path: PagePath.accountActivate, type: FetchType.PageFetching });

                if (authenticationStatus === STATUS.Authenticated) {
                  await actions.setPassword(fieldValues as ResetPassword);
                  navigate(PagePath.profile);
                } else {
                  await actions.setPasswordAndLogin(fieldValues as ResetPassword);
                  navigate(PagePath.root);
                }

                actions.stopLoader(PagePath.accountActivate);
              })}
            >
              <FieldController
                control={control}
                name="email"
                rules={new EmailValidator()}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    type="email"
                    label="Email"
                    responsive
                    validation={{
                      dirty: fieldState.isDirty || !!fieldState.error,
                      invalid: !!fieldState.error,
                      message: fieldState.error?.message ?? 'Enter your email',
                    }}
                  />
                )}
              />

              <FieldController
                control={control}
                name="password"
                rules={new RequiredValidator('Enter your password')}
                render={({ field, fieldState }) => (
                  <div style={{ width: '100%', marginBottom: 12 }}>
                    <TextField
                      {...field}
                      type="password"
                      label="Password"
                      responsive
                      validation={{
                        dirty: fieldState.isDirty || !!fieldState.error,
                        invalid: !!fieldState.error,
                        message: '',
                      }}
                    />

                    <PasswordStrength
                      password={fieldValues?.password}
                      onPercentage={percent => {
                        if (percent < 100) return;
                        actions.clearPasswordErrors();
                      }}
                    />
                  </div>
                )}
              />

              <FieldController
                control={control}
                name="confirmPassword"
                rules={new ConfirmationPasswordValidator('Enter your confirmation password', getValues('password'))}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    type="password"
                    label="Confirm Password"
                    responsive
                    validation={{
                      dirty: fieldState.isDirty || !!fieldState.error,
                      invalid: !!fieldState.error,
                      message: fieldState.error?.message ?? 'Enter your confirmation password',
                    }}
                  />
                )}
              />

              <Button fullWidthMobile type="button">
                Update
              </Button>

              {passwordErrors !== undefined &&
                passwordErrors?.length > 0 &&
                passwordErrors.map((error: string, index: number) => {
                  return (
                    <React.Fragment key={'error_' + index}>
                      <div className="formErrorMessage">{error}</div>
                    </React.Fragment>
                  );
                })}
            </form>
          </AccountPageLoaderLayer>
        </div>
      </div>
    </div>
  );
}
